import React, { useCallback, useMemo, useState } from 'react';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useNavigate } from 'react-router-dom';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import StatusUpdateModal from '../components/invoices/StatusUpdateModal';
import ReceiptsModal from '../components/invoices/ReceiptsModal';
import { CrmBadge, CrmButton, CrmTooltip } from '../components/ui';
import { CrmEntityListPage } from '../components/listing';
import { formatDate } from '../utils/dateFormatter';
import { formatCurrency } from '../utils/currencyUtils';
import { formatStatusLabel } from '../utils/statusFormatter';
import { getPendingPaymentReminders } from '../services/paymentReminderService';
import { getInvoiceById } from '../services/invoiceService';
import { useSettings } from '../context/SettingsContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const sourceLabel = (row) => {
  if (row?.source_label) return row.source_label;
  const source = String(row?.source_type || '').toUpperCase();
  if (source === 'FRONTEND_ORDER' || source === 'WEBSITE_ORDER') return 'Website';
  if (source === 'WORK_ORDER') return 'Catering';
  return source ? source.replace(/_/g, ' ') : 'Manual';
};

function Payments() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState(null);
  const [receiptsModalOpen, setReceiptsModalOpen] = useState(false);
  const [receiptsModalInvoice, setReceiptsModalInvoice] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const loadRows = useCallback(async ({ isAutoRefresh = false } = {}) => {
    if (!isAutoRefresh) setLoading(true);
    try {
      const response = await getPendingPaymentReminders();
      setRows(Array.isArray(response?.pending_payments) ? response.pending_payments : []);
    } catch (error) {
      setNotification({ open: true, message: error?.response?.data?.error || 'Failed to load pending payments', severity: 'error' });
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  }, []);

  useAutoRefresh(loadRows, { intervalMs: 15000 });

  const openReceipts = async (invoiceId) => {
    try {
      setReceiptsModalInvoice(await getInvoiceById(invoiceId));
      setReceiptsModalOpen(true);
    } catch {
      setNotification({ open: true, message: 'Failed to load receipts.', severity: 'error' });
    }
  };

  const statusOptions = useMemo(() => [...new Set(rows.map((row) => row.status).filter(Boolean))]
    .map((value) => ({ value, label: formatStatusLabel(value) })), [rows]);
  const sourceOptions = useMemo(() => [...new Set(rows.map(sourceLabel).filter(Boolean))]
    .map((value) => ({ value, label: value })), [rows]);

  const columns = useMemo(() => [
    {
      id: 'invoice_number', label: 'Invoice #', width: 160, minWidth: 130, maxWidth: 240,
      hideable: false, defaultPinned: true, pinnable: true, stopRowClick: true,
      renderCell: (row) => <button type="button" className="crm-table-primary-link" onClick={() => navigate(`/invoices/${row.id}`)}>{row.invoice_number || '—'}</button>,
    },
    { id: 'customer_name', label: 'Customer', width: 220, minWidth: 170, maxWidth: 380 },
    { id: 'source', label: 'Source', width: 130, minWidth: 110, maxWidth: 200, filterable: true, accessor: sourceLabel },
    { id: 'due_date', label: 'Due date', width: 150, minWidth: 130, maxWidth: 220, type: 'date', filterable: true, renderCell: (row) => formatDate(row.due_date) || '—' },
    { id: 'grand_total', label: 'Total', width: 150, minWidth: 130, maxWidth: 220, type: 'currency', accessor: (row) => Number(row.grand_total || 0), renderCell: (row) => formatCurrency(row.grand_total, settings?.currency_code) },
    { id: 'paid_amount', label: 'Paid', width: 150, minWidth: 130, maxWidth: 220, type: 'currency', accessor: (row) => Number(row.paid_amount || 0), renderCell: (row) => formatCurrency(row.paid_amount, settings?.currency_code) },
    { id: 'balance_due', label: 'Pending', width: 150, minWidth: 130, maxWidth: 220, type: 'currency', accessor: (row) => Number(row.balance_due || 0), renderCell: (row) => formatCurrency(row.balance_due, settings?.currency_code) },
    { id: 'status', label: 'Status', width: 140, minWidth: 120, maxWidth: 200, filterable: true, renderCell: (row) => <CrmBadge tone={row.status === 'part-payment' ? 'warning' : 'info'}>{formatStatusLabel(row.status)}</CrmBadge> },
    {
      id: 'row_actions', label: 'Actions', width: 112, minWidth: 112, maxWidth: 140,
      hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (row) => (
        <span className="crm-row-actions">
          <CrmTooltip content="Receive payment"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => { setPaymentInvoiceId(row.id); setPaymentModalOpen(true); }} aria-label="Receive payment"><AccountBalanceWalletOutlinedIcon /></CrmButton></CrmTooltip>
          <CrmTooltip content="View receipts"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openReceipts(row.id)} aria-label="View receipts"><ReceiptLongOutlinedIcon /></CrmButton></CrmTooltip>
        </span>
      ),
    },
  ], [navigate, settings?.currency_code]);

  const filters = useMemo(() => [
    { id: 'due', columnId: 'due_date', label: 'Due date', type: 'date-range', quick: true },
    { id: 'status', columnId: 'status', label: 'Status', type: 'select', options: statusOptions, quick: true },
    { id: 'source', columnId: 'source', label: 'Source', type: 'select', options: sourceOptions, quick: true, getValue: sourceLabel },
  ], [sourceOptions, statusOptions]);

  const summary = useMemo(() => [
    { label: 'Pending invoices', value: rows.length },
    { label: 'Total pending', value: formatCurrency(rows.reduce((sum, row) => sum + Number(row.balance_due || 0), 0), settings?.currency_code) },
  ], [rows, settings?.currency_code]);

  return (
    <div className="contacts-page">
      <CrmEntityListPage
        moduleKey="pending-payments"
        title="Pending Payments"
        itemLabel="payment"
        items={rows}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'due_date', direction: 'asc' }}
        loading={loading}
        onRefresh={loadRows}
        onSettings={() => navigate('/payment-reminders/settings')}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        summaryItems={summary}
        headerActions={<CrmButton size="small" onClick={() => navigate('/payments/history')}>Payment history</CrmButton>}
      />
      <StatusUpdateModal open={paymentModalOpen} invoiceId={paymentInvoiceId} onClose={() => { setPaymentModalOpen(false); setPaymentInvoiceId(null); }} onSuccess={(message) => { setNotification({ open: true, message, severity: 'success' }); loadRows(); }} onError={(message) => setNotification({ open: true, message, severity: 'error' })} />
      <ReceiptsModal open={receiptsModalOpen} invoice={receiptsModalInvoice} onClose={() => { setReceiptsModalOpen(false); setReceiptsModalInvoice(null); }} onError={(message) => setNotification({ open: true, message, severity: 'error' })} />
      <NotificationSnackbar {...notification} onClose={() => setNotification((previous) => ({ ...previous, open: false }))} />
    </div>
  );
}

export default Payments;
